import axios from 'axios';
import { deviceLabels } from '../models/devicesModels';
import { corsetToTranslationKey } from '../models/patientModels';
import { Role, ROLES } from 'libs/commons/src/lib/role-constants';

export const configureAxios = (token) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const formatDateString = (
  inputString: string,
  timezone: string
): string => {
  try {
    // Verificar formato de entrada
    const dateTimePattern = /(\d{8})-(\d{6})\.zip$/; // Formato esperado
    const match = inputString.match(dateTimePattern);

    if (!match) {
      console.error(
        'Invalid input string format. Expected YYYYMMDD-HHMMSS.zip'
      );
      throw new Error(`Invalid input string format: ${inputString}`);
    }

    const datePart = match[1]; // YYYYMMDD
    const timePart = match[2]; // HHMMSS

    // Crear fecha en formato ISO
    const formattedDateTime = `${datePart.substring(4, 8)}-${datePart.substring(2, 4)}-${datePart.substring(0, 2)}T${timePart.substring(0, 2)}:${timePart.substring(2, 4)}:${timePart.substring(4, 6)}Z`;

    const date = new Date(formattedDateTime);

    // Validar si se generó un objeto Date válido
    if (isNaN(date.getTime())) {
      console.error('Generated invalid date object');
      throw new RangeError(
        `Invalid date value for string: ${formattedDateTime}`
      );
    }

    // Formatear la fecha
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch (error) {
    console.error('Error formatting date string:', error.message);
    return 'Invalid date'; // Valor por defecto
  }
};

export const formatTimestamp = (
  value: string | Date | null | undefined,
  timezone: string
): string => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('es-ES', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatDateFromDB = (dbDate: string, timezone: string): string => {
  try {
    // Crear un objeto Date desde la fecha de la base de datos
    const date = new Date(dbDate);

    // Validar si la fecha es válida
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${dbDate}`);
    }

    // Formatear la fecha
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error.message);
    return dbDate; // Devolver la fecha original si hay un error
  }
};

export function formatTime(minutes) {
  const mins = parseFloat(minutes);
  if (isNaN(mins)) return 'Invalid time';

  const totalSeconds = mins * 60;
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  let result = '';
  if (days > 0) {
    result += `${days} d `;
  }
  if (hours > 0) {
    result += `${hours} h `;
  }
  if (remainingMinutes > 0) {
    result += `${remainingMinutes} m `;
  }
  result += `${seconds} s`;

  return result.trim();
}

export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'baja':
      return '#28A745';
    case 'media':
      return '#FFC107';
    case 'alta':
      return '#FF5722';
    case 'critica':
      return '#DC3545';
    default:
      return '#6C757D';
  }
};

export const getIncidentStatusColor = (status: string): string => {
  switch (status) {
    case 'abierta':
      return '#6C757D'; // Gris: "abierta" - Pendiente de atención (neutral)
    case 'en_proceso':
      return '#FFB300'; // Amarillo: "en proceso" - Progreso (necesita atención)
    case 'cerrada':
      return '#DC3545'; // Rojo: "cerrada" - Terminado (negativo, error o finalizado)
    case 'en_espera':
      return '#FF9800'; // Naranja: "en espera" - Pendiente de algo más (esperando)
    case 'verificada':
      return '#2196F3'; // Azul: "verificada" - Verificada (proceso terminado)
    case 'cancelada':
      return '#DC3545'; // Rojo: "cancelada" - Cancelado (finalizado, grave)
    case 'resuelta':
      return '#4CAF50'; // Verde: "resuelta" - Solucionado (éxito)
    case 'reabierto':
      return '#FF5722'; // Naranja: "reabierto" - Se ha reabierto (en atención de nuevo)
    default:
      return '#6C757D'; // Gris: Predeterminado (en caso de que el estado no esté definido)
  }
};

export const getRoleColor = (role: Role): string => {
  switch (role) {
    case ROLES.PERSONAL:
      return "#185FA5";
    case ROLES.CLINIC:
      return "#B91C1C";
    case ROLES.MAINTENANCE:
      return "#0F6E56";
    case ROLES.MARSI:
      return '#0a9f8b';
    case ROLES.TESTER:
      return '#4CAF50';
    case ROLES.COMMERCIAL:
      return "#7C3AED";
    case ROLES.DISTRIBUTOR:
      return '#FF9800';
    case ROLES.SPECIALIST_CLINIC:
      return '#ff7322';
    default:
      return '#6C757D';
  }
};
export const getDeviceColor = (device_type: string): string => {

  switch (device_type) {
    case "ATLAS":
      return "#B91C1C";
    case "EXPLORER":
      return "#185FA5";
    default:
      return '#6C757D';
  }
};

export const getDeviceModelLabel = (model: any) => {
  return deviceLabels.find((d) => d.value == model)?.label || model;
};

export const getCorsetModelLabel = (model: string) => {
  return (
    corsetToTranslationKey[model as keyof typeof corsetToTranslationKey] ||
    model
  );
};

/**
 * Devuelve la clave de traducción de una alarma según el modelo de dispositivo.
 * Si el modelo es Atlas, busca en alarmsAtlas, si no en alarms.
 */
export function getAlarmTranslationKey(eventId: string, isAtlas: boolean) {
  return isAtlas
    ? `pages.alarms.alarmsAtlas.${eventId}`
    : `pages.alarms.alarms.${eventId}`;
}

export const phonePatterns = {
  '+1': /^[2-9]\d{9}$/, // USA/Canada: 10 dígitos, empieza por 2-9
  '+44': /^7\d{9}$/, // UK: 10 dígitos, móviles empiezan por 7
  '+34': /^([6|7|9])\d{8}$/, // España: 9 dígitos, empieza por 6, 7 o 9
  '+52': /^\d{10}$/, // México: 10 dígitos
  '+55': /^\d{10,11}$/, // Brasil: 10 u 11 dígitos
  '+33': /^([6|7])\d{8}$/, // Francia: 9 dígitos, empieza por 6 o 7
  '+49': /^1\d{9}$/, // Alemania: 10 dígitos, móviles empiezan por 1
  '+81': /^\d{9,10}$/, // Japón: 9 o 10 dígitos
  '+61': /^4\d{8}$/, // Australia: 9 dígitos, móviles empiezan por 4
  '+91': /^[6-9]\d{9}$/, // India: 10 dígitos, empieza por 6-9
  '+39': /^3\d{9}$/, // Italia: 10 dígitos, móviles empiezan por 3
  '+7': /^9\d{9}$/, // Rusia: 10 dígitos, móviles empiezan por 9
  '+86': /^1\d{10}$/, // China: 11 dígitos, móviles empiezan por 1
  '+27': /^\d{9}$/, // Sudáfrica: 9 dígitos
  '+64': /^2\d{7,9}$/, // Nueva Zelanda: 8-10 dígitos, móviles empiezan por 2
  '+31': /^6\d{8}$/, // Países Bajos: 9 dígitos, móviles empiezan por 6
  '+46': /^7\d{8}$/, // Suecia: 9 dígitos, móviles empiezan por 7
  '+41': /^7\d{8}$/, // Suiza: 9 dígitos, móviles empiezan por 7
  '+48': /^\d{9}$/, // Polonia: 9 dígitos
  '+420': /^\d{9}$/, // Chequia: 9 dígitos
  '+351': /^9\d{8}$/, // Portugal: 9 dígitos, móviles empiezan por 9
  '+358': /^4\d{8}$/, // Finlandia: 9 dígitos, móviles empiezan por 4
  '+90': /^5\d{9}$/, // Turquía: 10 dígitos, móviles empiezan por 5
  '+62': /^8\d{8,11}$/, // Indonesia: 9-12 dígitos, móviles empiezan por 8
  '+380': /^\d{9}$/, // Ucrania: 9 dígitos
  '+234': /^[789]\d{9}$/, // Nigeria: 10 dígitos, móviles empiezan por 7,8,9
  '+82': /^1\d{9}$/, // Corea del Sur: 10 dígitos, móviles empiezan por 1
  '+84': /^\d{9,10}$/, // Vietnam: 9 o 10 dígitos
  '+65': /^\d{8}$/, // Singapur: 8 dígitos
  '+213': /^\d{9}$/, // Argelia: 9 dígitos
  '+36': /^\d{9}$/, // Hungría: 9 dígitos
  '+254': /^7\d{8}$/, // Kenia: 9 dígitos, móviles empiezan por 7
  '+374': /^\d{8}$/, // Armenia: 8 dígitos
};

export const ERROR_CONFIG: Record<string, { message: string }> = {
  VALIDATION_DUPLICATE_VALUE: {
    message: 'pages.patients.messages.emailAlreadyExists',
  },
  VALIDATION_INVALID_ORGANIZATION: {
    message: 'pages.patients.messages.organization_not_found',
  },
  VALIDATION_REQUIRED_FIELD: {
    message: 'pages.patients.formValidation.requiredField',
  },
  VALIDATION_INVALID_FORMAT: {
    message: 'pages.patients.formValidation.invalidFormat',
  },
  VALIDATION_INVALID_FORMAT_UPPERCASE: {
    message: 'pages.patients.formValidation.passwordUppercase',
  },
  VALIDATION_INVALID_FORMAT_LOWERCASE: {
    message: 'pages.patients.formValidation.passwordLowercase',
  },
  VALIDATION_INVALID_FORMAT_NUMBER: {
    message: 'pages.patients.formValidation.passwordNumber',
  },
  VALIDATIOON_BIRTH_DATE_UNDERAGE: {
    message: 'pages.patients.formValidation.underage',
  },
  SYSTEM_INTERNAL_ERROR: {
    message: 'pages.patients.messages.somethingUnexpectedHappened',
  },
};
